/**
 * DashboardSkeleton
 *
 * Branded loading state shown after login while the dashboard fetches
 * `/admin/dashboard` + `/quotations?limit=10`. Replaces the previous
 * generic "Loading..." spinner that felt like a crash for 10 seconds.
 *
 * Signals progress by rendering the exact final layout (heading + 4 stat
 * cards + table) as skeletons and captions the state with the tenant name
 * pulled from localStorage.tenant_slug ("Loading Cherry Holidays…").
 */
import React from 'react';
import Layout from '../Layout';
import { Card, CardContent, CardHeader } from '../ui/card';

const _titleCaseFromSlug = (slug) => {
  if (!slug) return 'your dashboard';
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const Bar = ({ w = 'w-24', h = 'h-4', extra = '' }) => (
  <div className={`${w} ${h} bg-slate-200/70 rounded animate-pulse ${extra}`} />
);

const DashboardSkeleton = () => {
  const tenantSlug = (typeof window !== 'undefined' && window.localStorage)
    ? window.localStorage.getItem('tenant_slug')
    : null;
  const tenantName = _titleCaseFromSlug(tenantSlug);

  return (
    <Layout>
      <div
        className="space-y-6 sm:space-y-8"
        data-testid="dashboard-skeleton"
        aria-busy="true"
        aria-live="polite"
      >
        {/* Header caption */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <Bar w="w-40" h="h-8" />
            <p
              className="text-sm text-navy-mid mt-2 font-ui"
              data-testid="dashboard-skeleton-caption"
            >
              Loading {tenantName}…
            </p>
          </div>
          <div className="flex gap-2">
            <Bar w="w-32" h="h-10" extra="rounded-md" />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[0, 1, 2, 3].map((i) => (
            <Card
              key={i}
              className="border-[rgba(0,200,232,0.15)]"
              data-testid={`dashboard-skeleton-stat-${i}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-3">
                    <Bar w="w-28" h="h-3" />
                    <Bar w="w-16" h="h-8" />
                  </div>
                  <div className="w-12 h-12 bg-slate-200/70 rounded-xl animate-pulse" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Quotations table skeleton */}
        <Card>
          <CardHeader>
            <Bar w="w-48" h="h-6" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-7 gap-4 border-b border-gray-200 pb-3">
                {[0, 1, 2, 3, 4, 5, 6].map((c) => (
                  <Bar key={c} w="w-full" h="h-3" />
                ))}
              </div>
              {[0, 1, 2, 3, 4].map((r) => (
                <div key={r} className="grid grid-cols-7 gap-4 border-b border-gray-100 py-3">
                  {[0, 1, 2, 3, 4, 5, 6].map((c) => (
                    <Bar key={c} w="w-full" h="h-3" />
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DashboardSkeleton;
